import { EmptyState, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@atlas/ui-components'
import { Spinner } from '@atlas/ui-primitives'
import { Link } from 'react-router-dom'
import { formatUtc } from '@/lib/format-utc'
import type { WorkfrontRunDTO, WorkfrontStepRunDTO } from '@/services/workfront.service'
import { humanize, RunStatusBadge } from './StatusBadges'

export interface RunsTableProps {
	runs: WorkfrontRunDTO[]
	isLoading?: boolean
	isError?: boolean
	hrefOf: (run: WorkfrontRunDTO) => string
}

/** `requests 5 · version 2`: a run's statistics, in the order the run recorded them. */
export const statsLine = (stats: Record<string, number>): string =>
	Object.entries(stats)
		.map(([name, value]) => `${humanize(name)} ${value}`)
		.join(' · ')

/** The adapter's runs, newest first, linking to their step runs. Prop-driven. */
export function RunsTable({ runs, isLoading, isError, hrefOf }: RunsTableProps) {
	if (isLoading) return <Spinner aria-label="Loading runs" />
	if (isError) return <EmptyState title="Could not load the runs" description="Try again in a moment." />
	if (runs.length === 0) return <EmptyState title="No runs" description="None match these filters." />
	return (
		<Table aria-label="Runs">
			<TableHeader>
				<TableRow>
					<TableHead>Process</TableHead>
					<TableHead>Trigger</TableHead>
					<TableHead>Status</TableHead>
					<TableHead>Started</TableHead>
					<TableHead>Finished</TableHead>
					<TableHead>Statistics</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{runs.map((run) => (
					<TableRow key={run.id}>
						<TableCell>
							<Link to={hrefOf(run)} className="font-medium hover:underline">
								{humanize(run.process)}
							</Link>
						</TableCell>
						<TableCell>{humanize(run.trigger)}</TableCell>
						<TableCell>
							<RunStatusBadge status={run.status} />
						</TableCell>
						<TableCell>{formatUtc(run.startedAt)}</TableCell>
						<TableCell>{run.finishedAt ? formatUtc(run.finishedAt) : '—'}</TableCell>
						<TableCell className="text-ds-text-muted">{statsLine(run.stats)}</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	)
}

/** A run's step runs: one per step on one subject, with its attempts and when it is retried next. */
export function StepRunsTable({ steps }: { steps: WorkfrontStepRunDTO[] }) {
	if (steps.length === 0) return <p className="text-ds-sm text-ds-text-muted">This run recorded no steps.</p>
	return (
		<Table aria-label="Step runs">
			<TableHeader>
				<TableRow>
					<TableHead>Step</TableHead>
					<TableHead>Subject</TableHead>
					<TableHead>Status</TableHead>
					<TableHead>Attempt</TableHead>
					<TableHead>Next retry</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{steps.map((step) => (
					<TableRow key={step.id}>
						<TableCell>{humanize(step.step)}</TableCell>
						<TableCell>
							<span className="text-ds-text-muted">{humanize(step.subjectType)}</span>{' '}
							<span className="font-mono">{step.subjectId}</span>
						</TableCell>
						<TableCell>
							<RunStatusBadge status={step.status} />
						</TableCell>
						<TableCell>
							{step.attempt} / {step.maxAttempts}
						</TableCell>
						<TableCell>{step.nextRetryAt ? formatUtc(step.nextRetryAt) : '—'}</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	)
}
